import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { GameState, SetupConfig, BetType, Hole18ScenariosData, RebuildRoundStateInput, ManualGameEvent, HoleScoreInput, RoundConfig } from '@/lib/golfEngine';
import { getPlayingOrder, getCompletedHolesInOrder, getBackHoles, getFrontHoles, hydrateGameStateFromRound } from '@/lib/golfEngine';
import { getGameEngine, isPressCapable } from '@/games/index';
import { SetupScreen } from '@/components/SetupScreen';
import { ScorecardShell } from '@/components/ScorecardShell';
import { PressRows } from '@/components/PressRows';
import { ResultsScreen } from '@/components/ResultsScreen';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, RotateCcw, Flag, BarChart3, Loader2, AlertTriangle, Ban, Play, Eraser, X, Share2, Trophy, Copy, Check, WifiOff, UserPlus, ShieldAlert, Wrench, MoreVertical, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { AuthButton } from '@/components/AuthButton';
import { BottomNav } from '@/components/BottomNav';
import { Hole18Scenarios } from '@/components/Hole18Scenarios';
import { BetStatusModal } from '@/components/BetStatusModal';
import { HoleDetailModal, type BulkHoleScores } from '@/components/HoleDetailModal';
import { ConfirmPressModal, type PendingPress } from '@/components/ConfirmPressModal';
import { AmbiguousIdentityModal } from '@/components/AmbiguousIdentityModal';
import { useAuth } from '@/hooks/use-auth';
import { useProfileCompletion } from '@/hooks/use-profile-completion';
import { ProfilePromptModal } from '@/components/ProfilePromptModal';
import { useRoundLifecycle } from '@/hooks/use-round-lifecycle';
import { createRound, syncHoleScores, syncPress, syncClearHoles, syncDeletePress, syncActiveHole, syncRoundSetup, finalizeRoundOnServer, unfinalizeRoundOnServer, loadRoundId, saveRoundId, loadRoundOwner, saveRoundOwner, loadRoundGroup, saveRoundGroup, loadRecoveryAttemptId, saveRecoveryAttemptId, type AmbiguousIdentityResult } from '@/lib/roundSync';
import { flushQueuedSyncs, getQueuedCount, hasSyncIssuesForRound, subscribeSyncStatus, clearQueueForRound, subscribeFailedSyncs, clearFailedSyncsForRound, requeueFailedHoleSync, subscribeFailedPressSyncs, requeueFailedPressSync, subscribePendingPressCount, subscribeFailedDeleteSyncs, requeueFailedDeleteSync, subscribeOwnershipErrors, subscribeRoundMissing, isRoundMissing, type FailedPressInfo, type FailedDeleteInfo } from '@/lib/syncQueue';
import { queryClient } from '@/lib/queryClient';

function extractRoundConfig(gs: GameState): RoundConfig {
  return {
    gameType: gs.gameType,
    matchFormat: gs.matchFormat,
    gameMode: gs.gameMode,
    teams: gs.teams,
    holes: gs.holes,
    stakes: gs.stakes,
    startingHole: gs.startingHole,
    courseDataId: gs.courseDataId,
    roundId: gs.roundId,
    isFinalized: gs.isFinalized,
  };
}

function extractScoreInputs(gs: GameState): HoleScoreInput[] {
  return gs.scores
    .filter(s => s.grossScore !== null)
    .map(s => ({ playerId: s.playerId, holeNumber: s.holeNumber, grossScore: s.grossScore }));
}

function extractManualGameEvents(gs: GameState): ManualGameEvent[] {
  if (gs.gameMode === 'TwoDownAuto') return [];
  return gs.presses.map(p => ({
    id: p.id,
    type: 'press' as const,
    betType: p.betType,
    startingHole: p.startingHole,
    initiatedBy: p.initiatedBy,
    stackCount: p.stackCount,
    parentPressId: p.parentPressId,
  }));
}

const STORAGE_KEY = 'twoDownAutoPlus_gameState';
const DISCARD_TOMBSTONE_KEY = 'twoDownAutoPlus_discard_tombstone';
const STALENESS_MS = 24 * 60 * 60 * 1000;
const BOOT_FETCH_TIMEOUT_MS = 10_000;
const BOOT_HANG_PANEL_MS = 20_000;

interface PersistedState {
  gameState: GameState;
  savedAt: number;
  savedByUserId?: string;
}

function loadPersistedState(): PersistedState | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    if (parsed && parsed.gameState) {
      return parsed as PersistedState;
    }
    if (parsed && parsed.gameType) {
      return { gameState: parsed as GameState, savedAt: 0 };
    }
  } catch (e) {
    console.error('Failed to load game state from localStorage:', e);
  }
  return null;
}

function saveGameState(state: GameState | null, userId?: string): void {
  try {
    if (state) {
      const persisted: PersistedState = {
        gameState: state,
        savedAt: Date.now(),
        savedByUserId: userId,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    console.error('Failed to save game state to localStorage:', e);
  }
}

function isDeepLinkSession(): boolean {
  const path = window.location.pathname;
  return path.startsWith('/invite/') || path.match(/^\/rounds\/[^/]+\/live$/) !== null;
}

// Clears only local resume state (persisted game state + round id/owner/group
// pointers). Never touches server data, auth/session, or account state.
function clearLocalRoundResumeState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    saveRoundId(null);
    saveRoundOwner(null);
    saveRoundGroup(null);
  } catch (e) {
    console.error('Failed to clear local round resume state:', e);
  }
}

type ViewMode = 'scorecard' | 'setup';
type BootStatus = 'booting' | 'prompt_resume' | 'network_error' | 'ready';

export function Scorecard() {
  const { toast } = useToast();
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('setup');
  const [bootStatus, setBootStatus] = useState<BootStatus>('booting');
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [reviewBannerDismissed, setReviewBannerDismissed] = useState(false);
  const [selectedHole, setSelectedHole] = useState<number | null>(null);
  const [showHoleDetail, setShowHoleDetail] = useState(false);
  const [showNewRoundDialog, setShowNewRoundDialog] = useState(false);
  const [hole18ScenariosData, setHole18ScenariosData] = useState<Hole18ScenariosData | null>(null);
  const [showBetStatus, setShowBetStatus] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [correctionMode, setCorrectionMode] = useState(false);
  const [correctionFromHole, setCorrectionFromHole] = useState<number | null>(null);
  const [showClearConfirmDialog, setShowClearConfirmDialog] = useState(false);
  const [showAbandonDialog, setShowAbandonDialog] = useState(false);
  const [showDiscardConfirmDialog, setShowDiscardConfirmDialog] = useState(false);
  const [pendingPress, setPendingPress] = useState<PendingPress | null>(null);
  const [showConfirmPress, setShowConfirmPress] = useState(false);
  const [loadRoundError, setLoadRoundError] = useState(false);
  const { user, isAuthenticated, isClerkSignedIn, isClerkLoaded, clerkTimedOut, isLoading: authLoading } = useAuth();
  // During an auth interruption user?.id becomes null/undefined, but the round's
  // persisted owner ID is still known from localStorage. Using it here ensures
  // queue entries created during the gap are stamped with the original commissioner
  // — not undefined — so they can never be flushed by a different account.
  const queueCommissionerId = user?.id ?? loadRoundOwner() ?? undefined;
  const [showOwnerMismatch, setShowOwnerMismatch] = useState(false);
  const [recoveryAttempted, setRecoveryAttempted] = useState(false);
  // Tracks whether the user tapped "Check Connection Again" on the Clerk-timeout
  // banner.  Used to derive clerkTimedOutBannerState without an extra effect.
  const [clerkRetryAttempted, setClerkRetryAttempted] = useState(false);
  // Gate B: recovery_required — local gameState says finalized but the server
  // says the round is still active.  This is the exact scenario from July 31:
  // handleFinalizeRound fell through to the guest path, writing isFinalized=true
  // locally while the server never received a finalize call.
  // When true, the recovery screen is rendered instead of ResultsScreen.
  // Cleared when the user re-finalizes successfully or resumes editing.
  const [isRecoveryRequired, setIsRecoveryRequired] = useState(false);
  // Initialised from localStorage so the banner survives app restart after a 404.
  const [roundMissing, setRoundMissing] = useState(() => {
    const rid = loadRoundId();
    return rid ? isRoundMissing(rid) : false;
  });
  const roundIdRef = useRef<string | null>(loadRoundId());
  const pendingSetupUpdateRef = useRef(false);
  const { lifecycle } = useRoundLifecycle(roundIdRef.current ?? undefined);
  const engine = getGameEngine(gameState?.gameType);
  const pressEngine = isPressCapable(engine) ? engine : null;
  const [pendingSyncs, setPendingSyncs] = useState(0);
  const [failedSyncHoles, setFailedSyncHoles] = useState<number[]>([]);
  const [failedPressSyncs, setFailedPressSyncs] = useState<FailedPressInfo[]>([]);
  const [failedDeleteSyncs, setFailedDeleteSyncs] = useState<FailedDeleteInfo[]>([]);
  const [pendingPressCount, setPendingPressCount] = useState(0);
  const [isResuming, setIsResuming] = useState(false);
  const [showMatchCreatedModal, setShowMatchCreatedModal] = useState(false);
  const [matchCreatedRoundId, setMatchCreatedRoundId] = useState<string | null>(null);
  const [matchCreatedGroupId, setMatchCreatedGroupId] = useState<string | null>(() => loadRoundGroup());
  const [shareLoading, setShareLoading] = useState(false);
  const [headerShareLoading, setHeaderShareLoading] = useState(false);
  const [localFinalizeConfirmed, setLocalFinalizeConfirmed] = useState(false);
  const [isUpgradingLocalRound, setIsUpgradingLocalRound] = useState(false);
  const [upgradeFailed, setUpgradeFailed] = useState(false);
  const autoUpgradeAttemptedRef = useRef(false);
  const [pendingConflicts, setPendingConflicts] = useState<AmbiguousIdentityResult | null>(null);
  const [isRetryingConflict, setIsRetryingConflict] = useState(false);
  const pendingConflictGameStateRef = useRef<GameState | null>(null);
  const [bootHangDetected, setBootHangDetected] = useState(false);
  const [showProfilePrompt, setShowProfilePrompt] = useState(
    () => import.meta.env.DEV && new URLSearchParams(window.location.search).get('preview') === 'profile'
  );
  const { shouldShowOnAppOpen, dismiss: dismissProfilePrompt } = useProfileCompletion();
  const [prefetchedInviteUrl, setPrefetchedInviteUrl] = useState<string | null>(null);
  const [invitePrefetchError, setInvitePrefetchError] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const bootRanRef = useRef(false);
  const [roundOwnerId, setRoundOwnerId] = useState<string | null>(() => loadRoundOwner());
  /** Gate B: true while the user is reviewing local scores from recovery_required.
   *  isRecoveryRequired stays true so "Back to Recovery" always works. */
  const [isReviewLocalScoresOpen, setIsReviewLocalScoresOpen] = useState(false);

  useEffect(() => {
    if (shouldShowOnAppOpen) setShowProfilePrompt(true);
  }, [shouldShowOnAppOpen]);

  useEffect(() => {
    if (bootRanRef.current) return;
    if (authLoading) return;
    bootRanRef.current = true;

    if (isDeepLinkSession()) {
      setBootStatus('ready');
      return;
    }

    const persisted = loadPersistedState();
    if (!persisted) {
      setBootStatus('ready');
      return;
    }

    const { gameState: savedState, savedAt, savedByUserId } = persisted;

    if (savedState.isFinalized) {
      if (!savedState.roundId) {
        // True guest round — no server record. Safe to clear and start fresh.
        saveGameState(null);
        saveRoundId(null);
        saveRoundOwner(null);
        saveRoundGroup(null);
        setBootStatus('ready');
        return;
      }
      // Server-backed round marked locally as finalized — restore and fall
      // through to the server-verify path below. The server is the source of
      // truth; clearing here would silently discard unsynced trailing scores.
    }

    const currentUserId = user?.id;
    const isStale = savedAt > 0 && (Date.now() - savedAt) > STALENESS_MS;

    if (!savedState.roundId) {
      if (currentUserId && savedByUserId && currentUserId !== savedByUserId) {
        saveGameState(null);
        saveRoundId(null);
        saveRoundOwner(null);
        saveRoundGroup(null);
      } else {
        setGameState(savedState);
        setViewMode('scorecard');
      }
      setBootStatus('ready');
      return;
    }

    if (!isAuthenticated) {
      // Server-backed round (roundId present) — NEVER clear based on staleness or
      // auth gap. The user may simply be resuming while Clerk is still hydrating.
      // Preserve the state so the queue can flush once auth returns.
      if (savedState.roundId) {
        setGameState(savedState);
        setViewMode('scorecard');
        setBootStatus('ready');
        return;
      }
      // True guest round — clear only if stale.
      if (isStale) {
        saveGameState(null);
        saveRoundId(null);
        saveRoundOwner(null);
        saveRoundGroup(null);
      } else {
        setGameState(savedState);
        setViewMode('scorecard');
      }
      setBootStatus('ready');
      return;
    }

    (async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), BOOT_FETCH_TIMEOUT_MS);
      try {
        const res = await fetch(`/api/rounds/${savedState.roundId}`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (res.status === 404 || res.status === 410) {
          saveGameState(null);
          saveRoundId(null);
          saveRoundOwner(null);
          saveRoundGroup(null);
          setBootStatus('ready');
          return;
        }
        if (!res.ok) throw new Error(`Server responded ${res.status}`);
        const data = await res.json();
        const isCommish = data.round?.userId === currentUserId;

        if (!isCommish) {
          saveGameState(null);
          saveRoundId(null);
          saveRoundOwner(null);
          saveRoundGroup(null);
          setBootStatus('ready');
          return;
        }

        setRoundOwnerId(data.round?.userId ?? null);
        saveRoundOwner(data.round?.userId ?? null);
        saveRoundGroup(data.round?.groupId ?? null);
        roundIdRef.current = savedState.roundId ?? null;

        // Gate B: recovery_required detection.
        // Local state says finalized but the server says the round is still
        // active. This is the July 31 scenario: the guest-path finalize wrote
        // isFinalized=true locally while the server never received a POST to
        // /api/rounds/:id/finalize. Do NOT clear localStorage — the scores and
        // queue are the only recovery path. Surface the recovery screen instead.
        if (savedState.isFinalized && !data.round?.isFinalized) {
          console.warn('[boot] recovery_required detected: local isFinalized=true but server says active', {
            roundId: savedState.roundId,
            serverIsFinalized: data.round?.isFinalized,
          });
          setGameState(savedState);
          setViewMode('scorecard'); // so the scorecard hooks initialise correctly
          if (data.round?.groupId) {
            setMatchCreatedGroupId(data.round.groupId);
            setMatchCreatedRoundId(savedState.roundId ?? null);
          }
          setIsRecoveryRequired(true);
          setBootStatus('ready');
          return;
        }

        setGameState(savedState);
        setViewMode('scorecard');
        if (data.round?.groupId) {
          setMatchCreatedGroupId(data.round.groupId);
          setMatchCreatedRoundId(savedState.roundId ?? null);
        }
        setBootStatus('prompt_resume');
      } catch (err) {
        console.warn('[boot] round-resume fetch failed or timed out', {
          roundId: savedState.roundId,
          aborted: controller.signal.aborted,
          online: navigator.onLine,
          error: err instanceof Error ? err.message : String(err),
        });
        setBootStatus('network_error');
      } finally {
        clearTimeout(timeoutId);
      }
    })();
  }, [authLoading, isAuthenticated, user]);

  // Belt-and-suspenders boot watchdog: independent of the fetch above, in
  // case ANY code path in the boot effect stalls (not just the network
  // request) — surfaces a recovery panel rather than a spinner with no exit.
  useEffect(() => {
    if (bootStatus !== 'booting') {
      setBootHangDetected(false);
      return;
    }
    const hangCheckStartedAt = Date.now();
    const t = setTimeout(() => {
      console.warn('[boot] boot spinner exceeded watchdog threshold', {
        watchdogThresholdMs: BOOT_HANG_PANEL_MS,
        actualElapsedMs: Date.now() - hangCheckStartedAt,
        online: navigator.onLine,
        roundId: roundIdRef.current,
      });
      setBootHangDetected(true);
    }, BOOT_HANG_PANEL_MS);
    return () => clearTimeout(t);
  }, [bootStatus]);

  const liveRoundRecoveryUrl = roundIdRef.current
    ? `${window.location.origin}/rounds/${roundIdRef.current}/live`
    : null;

  const handleCopyRoundLink = async () => {
    if (!liveRoundRecoveryUrl) return;
    try {
      await navigator.clipboard.writeText(liveRoundRecoveryUrl);
      toast({ title: 'Round link copied', duration: 3000 });
    } catch (e) {
      console.warn('[boot] failed to copy round link', e);
      toast({ title: 'Could not copy link', description: liveRoundRecoveryUrl, duration: 5000 });
    }
  };

  const handleOpenRoundNow = () => {
    if (!roundIdRef.current) return;
    console.warn('[boot] user forced prompt_resume from hang-recovery panel', {
      roundId: roundIdRef.current,
    });
    setBootStatus('prompt_resume');
  };

  const handleResetLocalAppData = () => {
    console.warn('[boot] user triggered local app data reset from hang-recovery panel', {
      roundId: roundIdRef.current,
    });
    clearLocalRoundResumeState();
    window.location.reload();
  };

  const handleKeepWaiting = () => {
    setBootHangDetected(false);
  };

  const handleResumeMatch = () => {
    setBootStatus('ready');
  };

  const handleDiscardMatch = () => {
    setShowDiscardConfirmDialog(true);
  };

  const executeDiscardMatch = () => {
    const snapshotKeys = [
      'twoDownAutoPlus_gameState',
      'twoDownAutoPlus_roundId',
      'twoDownAutoPlus_ownerId',
      'twoDownAutoPlus_groupId',
      'doublepress_sync_queue',
      'doublepress_failed_syncs',
      'twoDownAutoPlus_recoveryAttemptId',
    ];
    try {
      const snapshot: Record<string, string | null> = {};
      for (const k of snapshotKeys) {
        try { snapshot[k] = localStorage.getItem(k); } catch { snapshot[k] = null; }
      }
      localStorage.setItem(DISCARD_TOMBSTONE_KEY, JSON.stringify({
        snapshot,
        discardedAt: new Date().toISOString(),
        roundId: roundIdRef.current,
      }));
    } catch { /* tombstone write failure is non-fatal */ }

    if (roundIdRef.current) {
      clearQueueForRound(roundIdRef.current);
      clearFailedSyncsForRound(roundIdRef.current);
    }
    setGameState(null);
    setViewMode('setup');
    roundIdRef.current = null;
    saveRoundId(null);
    saveGameState(null);
    saveRoundOwner(null);
    saveRoundGroup(null);
    setShowDiscardConfirmDialog(false);
    setBootStatus('ready');
  };

  const handleNetworkRetry = () => {
    bootRanRef.current = false;
    setBootStatus('booting');
  };

  // Clerk-timeout recovery handler.
  // Invalidates /api/auth/user so the query re-fetches against whatever session
  // state is available — Clerk may have loaded by now (e.g. after a temporary
  // firewall drop), or the server session cookie may still be valid.
  // Does NOT clear localStorage, navigate, or discard any local state.
  const handleClerkRetry = () => {
    setClerkRetryAttempted(true);
    queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
  };

  // Derived display state for the Clerk-timeout sub-banner.
  // Drives which message and actions are shown without requiring an extra effect.
  //   'actionable'   — no retry attempted yet (or auth restored) → show Check Connection Again
  //   'retrying'     — retry in flight (authLoading is true after invalidation)
  //   'retry_failed' — retry completed but isAuthenticated is still false → show Round ID
  const clerkTimedOutBannerState: 'actionable' | 'retrying' | 'retry_failed' =
    !clerkRetryAttempted ? 'actionable' :
    authLoading ? 'retrying' :
    isAuthenticated ? 'actionable' :
    'retry_failed';

  const handleNetworkContinue = () => {
    const persisted = loadPersistedState();
    if (persisted) {
      setGameState(persisted.gameState);
      setViewMode('scorecard');
      roundIdRef.current = persisted.gameState.roundId ?? null;
    }
    setBootStatus('ready');
  };

  useEffect(() => {
    if (!showMatchCreatedModal || !matchCreatedRoundId) {
      setPrefetchedInviteUrl(null);
      setInvitePrefetchError(false);
      setShareCopied(false);
      return;
    }
    if (!matchCreatedGroupId) {
      setInvitePrefetchError(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const inviteRes = await fetch(`/api/groups/${matchCreatedGroupId}/invite`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ roundId: matchCreatedRoundId }),
        });
        if (!inviteRes.ok) throw new Error("Failed to create invite");
        const inviteData = await inviteRes.json();
        if (!cancelled) {
          setPrefetchedInviteUrl(`${window.location.origin}/invite/${inviteData.inviteToken}`);
        }
      } catch {
        if (!cancelled) setInvitePrefetchError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [showMatchCreatedModal, matchCreatedRoundId, matchCreatedGroupId]);

  const handleShareMatch = async () => {
    const url = prefetchedInviteUrl;
    if (!url) return;

    const shareText = (() => {
      if (!gameState || !gameState.teams || gameState.teams.length < 2) {
        return "Live scorecard for today's match on DoublePress.";
      }
      const fmtTeam = (t: typeof gameState.teams[0]) => t.players.map(p => p.name).join(' & ');
      return `Live scorecard for today's match between ${fmtTeam(gameState.teams[0])} vs ${fmtTeam(gameState.teams[1])}.`;
    })();

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Live Match on DoublePress",
          text: shareText,
          url,
        });
        setShowMatchCreatedModal(false);
        return;
      } catch (err: any) {
        if (err.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShowMatchCreatedModal(false), 1500);
    } catch {
      window.prompt("Copy this link:", url);
      setShowMatchCreatedModal(false);
    }
  };

  const handleHeaderShare = async () => {
    const groupId = matchCreatedGroupId ?? loadRoundGroup();
    const rid = roundIdRef.current ?? matchCreatedRoundId;
    if (!rid || !groupId) {
      toast({ title: "Share unavailable", description: "No group linked to this round.", variant: "destructive" });
      return;
    }
    setHeaderShareLoading(true);
    try {
      const inviteRes = await fetch(`/api/groups/${groupId}/invite`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roundId: rid }),
      });
      if (!inviteRes.ok) throw new Error("Failed to create invite");
      const inviteData = await inviteRes.json();
      const url = `${window.location.origin}/invite/${inviteData.inviteToken}`;

      const shareText = (() => {
        if (!gameState || !gameState.teams || gameState.teams.length < 2) {
          return "Live scorecard for today's match on DoublePress.";
        }
        const fmtTeam = (t: typeof gameState.teams[0]) => t.players.map(p => p.name).join(' & ');
        return `Live scorecard for today's match between ${fmtTeam(gameState.teams[0])} vs ${fmtTeam(gameState.teams[1])}.`;
      })();

      if (navigator.share) {
        try {
          await navigator.share({ title: "Live Match on DoublePress", text: shareText, url });
          return;
        } catch (err: any) {
          if (err.name === "AbortError") return;
        }
      }

      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied!", description: "Invite link copied to clipboard." });
    } catch {
      toast({ title: "Share failed", description: "Could not generate invite link.", variant: "destructive" });
    } finally {
      setHeaderShareLoading(false);
    }
  };

  useEffect(() => {
    return subscribeSyncStatus(setPendingSyncs);
  }, []);

  useEffect(() => {
    const rid = roundIdRef.current;
    if (!rid) return;
    return subscribeFailedSyncs(rid, setFailedSyncHoles);
  // roundIdRef.current is not reactive; the round ID is stable once a round
  // is in progress, so this effect runs once the ref is populated.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundIdRef.current]);

  useEffect(() => {
    const rid = roundIdRef.current;
    if (!rid) return;
    return subscribeFailedPressSyncs(rid, setFailedPressSyncs);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundIdRef.current]);

  useEffect(() => {
    const rid = roundIdRef.current;
    if (!rid) return;
    return subscribeFailedDeleteSyncs(rid, setFailedDeleteSyncs);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundIdRef.current]);

  useEffect(() => {
    const rid = roundIdRef.current;
    if (!rid) return;
    return subscribePendingPressCount(rid, setPendingPressCount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundIdRef.current]);

  const isReconcilingRef = useRef(false);
  const prevSyncCountRef = useRef(0);
  const needsReconcileRef = useRef(false);

  const doReconcile = useCallback(async () => {
    const rid = roundIdRef.current;
    if (!rid || isReconcilingRef.current) {
      needsReconcileRef.current = true;
      return;
    }
    isReconcilingRef.current = true;
    needsReconcileRef.current = false;

    try {
      const res = await fetch(`/api/rounds/${rid}/live`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      const hydrated = await hydrateGameStateFromRound(data);
      setGameState(prev => {
        if (!prev) return hydrated;
        if (pendingSetupUpdateRef.current) return { ...hydrated, teams: prev.teams };
        return hydrated;
      });
    } catch (err) {
      console.warn('[Scorecard] Reconciliation fetch failed:', err);
    } finally {
      isReconcilingRef.current = false;
      if (needsReconcileRef.current) {
        needsReconcileRef.current = false;
        doReconcile();
      }
    }
  }, []);

  useEffect(() => {
    const wasSyncing = prevSyncCountRef.current > 0;
    prevSyncCountRef.current = pendingSyncs;

    if (pendingSyncs !== 0 || !wasSyncing) return;
    if (!isAuthenticated || !roundIdRef.current) return;
    if (!gameState || gameState.isFinalized) return;

    doReconcile();
  }, [pendingSyncs, isAuthenticated, gameState, doReconcile]);

  // Subscribe to 403 ownership errors from the sync queue so we can surface
  // the commissioner-mismatch banner without coupling the queue to the UI.
  useEffect(() => {
    const unsub = subscribeOwnershipErrors((_body) => {
      setShowOwnerMismatch(true);
    });
    return unsub;
  }, []);

  // Subscribe to round_missing events (404/410 from the server).
  // Also re-check the store on every mount in case this was detected in a
  // prior session and stored to localStorage.
  useEffect(() => {
    const rid = roundIdRef.current;
    if (rid && isRoundMissing(rid)) setRoundMissing(true);
    const unsub = subscribeRoundMissing((missingId) => {
      if (missingId === roundIdRef.current) setRoundMissing(true);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !roundIdRef.current) return;
    // Commissioner-binding check: refuse to flush if the currently signed-in
    // user is different from the round's recorded owner. This prevents one
    // user's scores from being flushed under another user's session.
    const storedOwner = loadRoundOwner();
    if (storedOwner && user?.id && storedOwner !== user.id) {
      setShowOwnerMismatch(true);
      return;
    }
    flushQueuedSyncs(user?.id);
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (bootStatus !== 'ready') return;
    saveGameState(gameState, user?.id);
  }, [gameState, bootStatus, user?.id]);

  const showReviewBanner = !!gameState && !gameState.isFinalized && engine.isRoundComplete(gameState) && !reviewBannerDismissed;

  useEffect(() => {
    if (!gameState || gameState.isFinalized || gameState.gameMode !== 'OnDemandPress') {
      setHole18ScenariosData(null);
      return;
    }

    const scenariosData = pressEngine?.computeHole18Scenarios(gameState) ?? null;
    setHole18ScenariosData(scenariosData);
  }, [gameState]);

  // ── Auto-upgrade: promote local round to server-backed when auth resolves ──
  // This recovers the scenario where a round was started before Clerk loaded,
  // auth resolves mid-round, we silently create the server round and sync scores.
  const runUpgrade = async (gs: typeof gameState) => {
    if (!gs || gs.isFinalized) return;
    setIsUpgradingLocalRound(true);
    setUpgradeFailed(false);
    try {
      const organizerName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || undefined;
      const result = await createRound(gs, user?.personId ?? undefined, organizerName);
      if (result && 'error' in result && result.error === 'ambiguous_identity') {
        // Surface the recovery modal instead of a dead-end toast. The modal
        // lets the commish pick the right person for each conflicting slot
        // and retries createRound with explicit personIds — no score loss.
        pendingConflictGameStateRef.current = gs;
        setPendingConflicts(result);
        setUpgradeFailed(false);
      } else if (result && 'roundId' in result) {
        roundIdRef.current = result.roundId;
        saveRoundId(result.roundId);
        setGameState((prev) => prev ? { ...prev, roundId: result.roundId } : prev);
        setMatchCreatedRoundId(result.roundId);
        setMatchCreatedGroupId(result.groupId);
        const scoredHoles = [...new Set(
          gs.scores.filter((s) => s.grossScore !== null).map((s) => s.holeNumber)
        )];
        for (const h of scoredHoles) {
          syncHoleScores(result.roundId, h, gs, queueCommissionerId);
        }
        toast({
          title: '✓ Round saved to your account',
          description: 'Your scores are syncing now.',
          duration: 4000,
        });
      } else {
        setUpgradeFailed(true);
        toast({
          title: 'Could not save round',
          description: 'Check your connection and try again.',
          variant: 'destructive',
          duration: 6000,
        });
      }
    } catch {
      setUpgradeFailed(true);
    } finally {
      setIsUpgradingLocalRound(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    if (autoUpgradeAttemptedRef.current) return;
    if (roundIdRef.current) return; // already server-backed
    if (!gameState || gameState.isFinalized) return;
    if (viewMode !== 'scorecard') return;
    if (bootStatus !== 'ready') return;

    autoUpgradeAttemptedRef.current = true;
    runUpgrade(gameState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, viewMode, bootStatus]);

  const handleStartRound = async (config: SetupConfig) => {
    const initialState = engine.initializeGameState(config);
    // Pre-persist before any render so a crash during the first scorecard
    // render (e.g. React #310) doesn't strand the commish on a blank screen.
    // The useEffect that normally persists runs *after* a successful render;
    // if the render throws, the effect never fires and the game is lost.
    // Writing here guarantees the resume prompt appears after ErrorBoundary reload.
    saveGameState(initialState, user?.id ?? undefined);
    setGameState(initialState);
    setReviewBannerDismissed(false);

    if (isAuthenticated) {
      const organizerName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || undefined;
      const result = await createRound(initialState, user?.personId ?? undefined, organizerName);
      if (result && 'error' in result && result.error === 'ambiguous_identity') {
        pendingConflictGameStateRef.current = initialState;
        setPendingConflicts(result);
        // Falls through to local-only mode below — round is NOT server-backed.
        // The AmbiguousIdentityModal now provides the recovery path.
      } else if (result && 'error' in result && result.error === 'auth') {
        toast({
          title: 'Session expired',
          description: result.message,
          variant: 'destructive',
          duration: 8000,
        });
        // Falls through to local-only mode below — round is NOT server-backed,
        // so "Send Live Scoring" will correctly stay hidden until the user
        // signs back in and starts a new round.
      } else if (result && 'roundId' in result) {
        roundIdRef.current = result.roundId;
        saveRoundId(result.roundId);
        setRoundOwnerId(user?.id ?? null);
        saveRoundOwner(user?.id ?? null);
        setGameState((prev) => prev ? { ...prev, roundId: result.roundId } : prev);
        setMatchCreatedRoundId(result.roundId);
        setMatchCreatedGroupId(result.groupId);
        saveRoundGroup(result.groupId ?? null);
        queryClient.invalidateQueries({ queryKey: ['/api/contacts/recents'] });
        setViewMode('scorecard');
        setShowMatchCreatedModal(true);
        return;
      }
    }

    setViewMode('scorecard');
  };

  // ── Ambiguous identity recovery ──────────────────────────────────────────
  // Called when the commish confirms their candidate selections in the modal.
  // Patches the stored GameState with explicit personIds for each resolved
  // slot, retries createRound, and — on success — syncs any already-entered
  // scores (the stranded-round recovery path).
  const handleConflictResolved = async (resolutions: Record<string, string>) => {
    const gs = pendingConflictGameStateRef.current;
    if (!gs) return;
    setIsRetryingConflict(true);
    try {
      // Patch the GameState: inject the resolved personId into each conflicting slot.
      const patchedGs: GameState = {
        ...gs,
        teams: gs.teams.map((team, teamIdx) => ({
          ...team,
          players: team.players.map((player, posIdx) => {
            const teamKey = teamIdx === 0 ? 'A' : 'B';
            const key = `${teamKey}-${posIdx}`;
            const resolvedPersonId = resolutions[key];
            return resolvedPersonId ? { ...player, personId: resolvedPersonId } : player;
          }),
        })),
      };
      // ── Recovery idempotency key ─────────────────────────────────────────────
      // Persist the key to localStorage BEFORE sending the POST.  If the server
      // commits but the response is lost (timeout / reload), the next retry sends
      // the same key and the server returns the existing round instead of creating
      // a duplicate.  Cleared immediately after a confirmed success.
      let recoveryKey = loadRecoveryAttemptId();
      if (!recoveryKey) {
        recoveryKey = crypto.randomUUID();
        saveRecoveryAttemptId(recoveryKey);
      }
      const organizerName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || undefined;
      const result = await createRound(patchedGs, user?.personId ?? undefined, organizerName, recoveryKey);
      if (result && 'roundId' in result) {
        // Success — clear the idempotency key now that the roundId is confirmed.
        saveRecoveryAttemptId(null);
        roundIdRef.current = result.roundId;
        saveRoundId(result.roundId);
        setRoundOwnerId(user?.id ?? null);
        saveRoundOwner(user?.id ?? null);
        setGameState((prev) => prev ? { ...prev, roundId: result.roundId } : prev);
        setMatchCreatedRoundId(result.roundId);
        setMatchCreatedGroupId(result.groupId);
        saveRoundGroup(result.groupId ?? null);
        // Sync any scores already entered — the key recovery step for stranded rounds.
        const scoredHoles = [...new Set(
          patchedGs.scores.filter(s => s.grossScore !== null).map(s => s.holeNumber)
        )];
        for (const h of scoredHoles) {
          syncHoleScores(result.roundId, h, patchedGs, queueCommissionerId);
        }
        queryClient.invalidateQueries({ queryKey: ['/api/contacts/recents'] });
        setPendingConflicts(null);
        pendingConflictGameStateRef.current = null;
        setUpgradeFailed(false);
        toast({
          title: '✓ Round saved',
          description: scoredHoles.length > 0
            ? `Conflict resolved. Syncing ${scoredHoles.length} hole${scoredHoles.length === 1 ? '' : 's'}.`
            : 'Conflict resolved. Round is now saved to your account.',
          duration: 4000,
        });
      } else if (result && 'error' in result && result.error === 'ambiguous_identity') {
        // Still ambiguous — update the modal with the fresh conflict set.
        pendingConflictGameStateRef.current = patchedGs;
        setPendingConflicts(result);
        toast({
          title: 'Still conflicted',
          description: result.message,
          variant: 'destructive',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Could not save round',
          description: 'Check your connection and try again.',
          variant: 'destructive',
          duration: 6000,
        });
      }
    } catch {
      toast({
        title: 'Could not save round',
        description: 'Check your connection and try again.',
        variant: 'destructive',
        duration: 6000,
      });
    } finally {
      setIsRetryingConflict(false);
    }
  };

  // ── Export recovery backup ───────────────────────────────────────────────
  // Captures all six localStorage keys that constitute the full round state,
  // plus app version and timestamp.  Uses the Web Share API on iOS (text/plain
  // share sheet) with a clipboard-copy fallback.  Does NOT modify any state.
  const handleExportBackup = async () => {
    const keys = [
      'twoDownAutoPlus_gameState',
      'twoDownAutoPlus_roundId',
      'twoDownAutoPlus_ownerId',
      'twoDownAutoPlus_groupId',
      'doublepress_sync_queue',
      'doublepress_failed_syncs',
      'twoDownAutoPlus_recoveryAttemptId',
    ];
    const backup: Record<string, string | null> = {};
    for (const k of keys) {
      try { backup[k] = localStorage.getItem(k); } catch { backup[k] = null; }
    }
    const exportObj = {
      _meta: { appVersion: '1.0.0', exportedAt: new Date().toISOString(), note: 'DoublePress recovery backup — restore via Application → Local Storage' },
      ...backup,
    };
    const text = JSON.stringify(exportObj, null, 2);
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: 'DoublePress recovery backup', text });
        return;
      }
    } catch { /* fall through to clipboard */ }
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Backup copied', description: 'Paste the text somewhere safe (Notes, email, etc.) before retrying.', duration: 5000 });
    } catch {
      toast({ title: 'Export failed', description: 'Open DevTools → Application → Local Storage to copy the data manually.', variant: 'destructive', duration: 8000 });
    }
  };

  const handleConflictDismissed = () => {
    setPendingConflicts(null);
    pendingConflictGameStateRef.current = null;
    // Round stays in local-only mode — no data loss. Upgrade banner remains
    // so the commish can retry later via the "Try again" button.
    setUpgradeFailed(true);
  };

  const handleResumeRound = async (config: SetupConfig) => {
    if (!gameState) return;
    const updated = engine.updateGameStateSettings(gameState, config);
    const rid = roundIdRef.current;
    if (rid && isAuthenticated) {
      pendingSetupUpdateRef.current = true;
      const ok = await syncRoundSetup(rid, updated);
      pendingSetupUpdateRef.current = false;
      if (!ok) {
        toast({
          title: 'Setup update failed',
          description: 'Could not save handicap changes to the server. Please try again.',
          variant: 'destructive',
        });
        return;
      }
    }
    setGameState(updated);
    setViewMode('scorecard');
  };

  const handleLoadRound = async (roundId: string) => {
    setLoadRoundError(false);
    setIsResuming(true);
    try {
      await flushQueuedSyncs(user?.id);
      const res = await fetch(`/api/rounds/${roundId}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`${res.status}`);
      const roundData = await res.json();
      const hydrated = await hydrateGameStateFromRound(roundData);
      setRoundOwnerId(roundData.round?.userId ?? null);
      saveRoundOwner(roundData.round?.userId ?? null);

      const lastActiveHole = roundData.round?.lastActiveHole as number | null;
      const playingOrder = getPlayingOrder(hydrated.startingHole);
      const firstIncomplete = playingOrder.find(h =>
        hydrated.teams.flatMap(t => t.players).some(p => {
          const sc = hydrated.scores.find(s => s.playerId === p.id && s.holeNumber === h);
          return !sc || sc.grossScore === null;
        })
      );
      const resumeHole = firstIncomplete ?? lastActiveHole ?? playingOrder[0];

      roundIdRef.current = roundId;
      saveRoundId(roundId);
      setGameState({ ...hydrated, currentHole: resumeHole });
      setReviewBannerDismissed(false);
      setViewMode('scorecard');
      if (!hydrated.isFinalized && resumeHole) {
        setSelectedHole(resumeHole);
      }
    } catch (err) {
      console.error('[Scorecard] Failed to load round:', err);
      setLoadRoundError(true);
      throw err;
    } finally {
      setIsResuming(false);
    }
  };

  const handleBackToSetup = () => {
    const hasEnteredScores = gameState?.scores?.some(s => s.grossScore !== null) ?? false;
    if (hasEnteredScores) {
      setShowAbandonDialog(true);
      return;
    }
    setViewMode('setup');
  };

  const confirmAbandonRound = () => {
    setShowAbandonDialog(false);
    setViewMode('setup');
  };

  const handleCancelEdit = () => {
    setViewMode('scorecard');
  };

  const replayFromState = useCallback((stateWithNewScores: GameState): GameState => {
    const roundConfig = extractRoundConfig(stateWithNewScores);
    const scoreInputs = extractScoreInputs(stateWithNewScores);
    const manualEvents = extractManualGameEvents(stateWithNewScores);
    const { gameState: rebuilt } = engine.rebuildRoundState({
      roundConfig,
      scores: scoreInputs,
      manualGameEvents: manualEvents,
    });
    return rebuilt;
  }, [engine]);

  const handleScoreChange = (playerId: string, holeNumber: number, score: number | null) => {
    if (!gameState || gameState.isFinalized) return;
    const withNewScore = engine.updateScore(gameState, playerId, holeNumber, score);
    const rebuilt = replayFromState(withNewScore);
    setGameState(rebuilt);

    const rid = roundIdRef.current;
    if (rid && score !== null) {
      syncHoleScores(rid, holeNumber, rebuilt, queueCommissionerId);
      syncActiveHole(rid, rebuilt.currentHole, queueCommissionerId);
    }
  };

  const handleBulkScoreChange = (payload: BulkHoleScores) => {
    if (!gameState || gameState.isFinalized) return;
    let current = gameState;
    for (const [playerId, score] of Object.entries(payload.scores)) {
      current = engine.updateScore(current, playerId, payload.holeNumber, score);
    }
    const rebuilt = replayFromState(current);
    setGameState(rebuilt);

    const rid = roundIdRef.current;
    if (rid) {
      syncHoleScores(rid, payload.holeNumber, rebuilt, queueCommissionerId);
      syncActiveHole(rid, rebuilt.currentHole, queueCommissionerId);
    }
  };

  const executePress = (betType: BetType, side: 'top' | 'bottom', count: number = 1, holeOverride?: number) => {
    if (!gameState || gameState.isFinalized) return;
    const hole = holeOverride ?? selectedHole ?? undefined;
    if (!pressEngine) return;
    const updated = pressEngine.addManualPress(gameState, betType, side, count, hole);
    const rebuilt = replayFromState(updated);
    setGameState(rebuilt);

    const rid = roundIdRef.current;
    if (rid) {
      // Only sync when a genuinely new press was added (has a new client-generated ID).
      // If addManualPress took the stack-increment path (existingPress.stackCount++),
      // no new press row was created — newPress is undefined — so we skip the sync.
      // Syncing without a clientId would cause the server to create a UUID press
      // that can never be matched by syncDeletePress, producing a zombie on reload.
      const newPress = updated.presses.find(
        p => p.betType === betType && p.initiatedBy === side &&
          !gameState.presses.some(old => old.id === p.id)
      );
      if (newPress) {
        syncPress(rid, betType, side, newPress.startingHole, count, undefined, newPress.id, queueCommissionerId);
      }
    }
  };

  const handleAddPress = (betType: BetType, side: 'top' | 'bottom', count: number = 1, holeOverride?: number) => {
    setPendingPress({ type: 'new', betType, side, count, holeOverride });
    setShowConfirmPress(true);
  };

  const executePressExisting = (pressId: string, side: 'top' | 'bottom') => {
    if (!gameState || gameState.isFinalized) return;
    if (!pressEngine) return;
    const updated = pressEngine.addPressOnExistingPress(gameState, pressId, side);
    const rebuilt = replayFromState(updated);
    setGameState(rebuilt);

    const rid = roundIdRef.current;
    if (rid && isAuthenticated) {
      const parentPress = gameState.presses.find((p) => p.id === pressId);
      if (parentPress) {
        const childPress = updated.presses.find(
          p => p.parentPressId === pressId && p.initiatedBy === side &&
            !gameState.presses.some(old => old.id === p.id)
        );
        const syncHole = childPress?.startingHole ?? updated.currentHole;
        syncPress(rid, parentPress.betType, side, syncHole, 1, pressId, childPress?.id, queueCommissionerId);
      }
    }
  };

  const handlePressExistingPress = (pressId: string, side: 'top' | 'bottom') => {
    if (!gameState) return;
    const press = gameState.presses.find(p => p.id === pressId);
    const betLabel = press ? (press.betType === 'Front' ? 'F' : press.betType === 'Back' ? 'B' : 'Over') : '';
    const pressIndex = press ? gameState.presses.filter(p => p.betType === press.betType).indexOf(press) + 1 : 0;
    setPendingPress({ type: 'existing', pressId, side, label: `P${pressIndex} ${betLabel}` });
    setShowConfirmPress(true);
  };

  const handleConfirmPress = () => {
    if (!pendingPress) return;
    if (pendingPress.type === 'new') {
      executePress(pendingPress.betType, pendingPress.side, pendingPress.count, pendingPress.holeOverride);
    } else {
      executePressExisting(pendingPress.pressId, pendingPress.side);
    }
    setShowConfirmPress(false);
    setPendingPress(null);
  };

  const handleDeletePress = useCallback((pressId: string) => {
    if (!gameState || gameState.isFinalized) return;
    const pressToDelete = gameState.presses.find(p => p.id === pressId);
    if (!pressToDelete) return;

    const idsToRemove = new Set<string>([pressId]);
    let added = true;
    while (added) {
      added = false;
      for (const p of gameState.presses) {
        if (p.parentPressId && idsToRemove.has(p.parentPressId) && !idsToRemove.has(p.id)) {
          idsToRemove.add(p.id);
          added = true;
        }
      }
    }

    const filteredPresses = gameState.presses.filter(p => !idsToRemove.has(p.id));

    const modifiedState: GameState = { ...gameState, presses: filteredPresses };
    const rebuilt = replayFromState(modifiedState);
    setGameState(rebuilt);

    const rid = roundIdRef.current;
    if (rid) {
      syncDeletePress(rid, pressId, queueCommissionerId);
    }
  }, [gameState, replayFromState]);

  const handleClearHole = useCallback((holeNumber: number) => {
    if (!gameState || gameState.isFinalized) return;
    let current = gameState;
    for (const player of gameState.teams.flatMap(t => t.players)) {
      current = engine.updateScore(current, player.id, holeNumber, null);
    }

    const removedIds = new Set<string>(current.presses.filter(p => p.startingHole === holeNumber).map(p => p.id));
    let prevSize = 0;
    while (removedIds.size !== prevSize) {
      prevSize = removedIds.size;
      current.presses.forEach(p => {
        if (p.parentPressId && removedIds.has(p.parentPressId)) removedIds.add(p.id);
      });
    }
    if (removedIds.size > 0) {
      current = { ...current, presses: current.presses.filter(p => !removedIds.has(p.id)) };
    }

    const rebuilt = replayFromState(current);
    setGameState(rebuilt);
    setShowHoleDetail(false);

    const rid = roundIdRef.current;
    if (rid) {
      syncClearHoles(rid, [holeNumber], queueCommissionerId);
    }
  }, [gameState, engine, replayFromState]);

  const handleCorrectEarlierHole = useCallback(() => {
    setShowHoleDetail(false);
    setCorrectionMode(true);
    setCorrectionFromHole(null);
  }, []);

  const handleCorrectionHoleSelect = useCallback((holeNumber: number) => {
    if (!gameState) return;
    const completedHoleNums = new Set(getCompletedHolesInOrder(gameState));
    if (!completedHoleNums.has(holeNumber)) return;
    setCorrectionFromHole(holeNumber);
    setShowClearConfirmDialog(true);
  }, [gameState]);

  const handleConfirmClearFrom = useCallback(() => {
    if (!gameState || correctionFromHole === null) return;
    const playingOrder = getPlayingOrder(gameState.startingHole);
    const fromIdx = playingOrder.indexOf(correctionFromHole);
    const completedHoleNums = new Set(getCompletedHolesInOrder(gameState));
    const holesToClear = playingOrder.filter((h, i) => i >= fromIdx && completedHoleNums.has(h));

    let current = gameState;
    for (const holeNum of holesToClear) {
      for (const player of gameState.teams.flatMap(t => t.players)) {
        current = engine.updateScore(current, player.id, holeNum, null);
      }
    }

    const holeSet = new Set(holesToClear);
    const removedIds = new Set<string>(current.presses.filter(p => holeSet.has(p.startingHole)).map(p => p.id));
    let prevSize = 0;
    while (removedIds.size !== prevSize) {
      prevSize = removedIds.size;
      current.presses.forEach(p => {
        if (p.parentPressId && removedIds.has(p.parentPressId)) removedIds.add(p.id);
      });
    }
    if (removedIds.size > 0) {
      current = { ...current, presses: current.presses.filter(p => !removedIds.has(p.id)) };
    }

    const rebuilt = replayFromState(current);
    setGameState(rebuilt);
    setCorrectionMode(false);
    setCorrectionFromHole(null);
    setShowClearConfirmDialog(false);

    const rid = roundIdRef.current;
    if (rid && holesToClear.length > 0) {
      syncClearHoles(rid, holesToClear, queueCommissionerId);
    }
  }, [gameState, correctionFromHole, engine, replayFromState]);

  const handleCancelCorrection = useCallback(() => {
    setCorrectionMode(false);
    setCorrectionFromHole(null);
    setShowClearConfirmDialog(false);
  }, []);

  const handleRefreshStrokeIndexes = useCallback(async () => {
    if (!gameState?.courseDataId) return;
    try {
      const res = await fetch('/api/courses/local', { cache: 'no-store', credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch course data');
      const allCourses: any[] = await res.json();
      const course = allCourses.find((c: any) => c.id === gameState.courseDataId);
      if (!course) {
        toast({ title: 'Course not found', description: 'Could not find this course in the database.', variant: 'destructive' });
        return;
      }
      const firstPlayer = gameState.teams.flatMap((t: any) => t.players)[0];
      const teeBoxId = firstPlayer?.teeBoxId;
      const tee = course.tees?.find((t: any) => t.id === teeBoxId) ?? course.tees?.[0];
      if (!tee?.holes?.length) {
        toast({ title: 'Tee data missing', description: 'No hole data found for this tee.', variant: 'destructive' });
        return;
      }
      const updatedHoles = gameState.holes.map((h: any, i: number) => {
        const dbHole = tee.holes.find((dh: any) => dh.holeNumber === h.number) ?? tee.holes[i];
        return dbHole ? { ...h, strokeIndex: dbHole.handicapIndex } : h;
      });
      const updated = { ...gameState, holes: updatedHoles };
      setGameState(updated);
      saveGameState(updated, user?.id);
      toast({ title: 'Stroke indexes refreshed', description: `Updated hole SI values from the database for ${tee.teeName ?? tee.teeColor ?? 'selected tee'}.` });
    } catch (err) {
      console.error('[fixStrokes]', err);
      toast({ title: 'Refresh failed', description: 'Could not load updated course data. Check your connection.', variant: 'destructive' });
    }
  }, [gameState, toast, user?.id]);

  const handleNewRound = () => {
    if (roundIdRef.current) {
      clearQueueForRound(roundIdRef.current);
    }
    setGameState(null);
    setReviewBannerDismissed(false);
    setHole18ScenariosData(null);
    setViewMode('setup');
    setShowNewRoundDialog(false);
    roundIdRef.current = null;
    saveRoundId(null);
    saveRoundOwner(null);
    saveRoundGroup(null);
  };

  const handleConfirmNewRound = () => {
    if (gameState && !gameState.isFinalized) {
      setShowNewRoundDialog(true);
    } else {
      handleNewRound();
    }
  };

  const handleFinalizeRound = async () => {
    if (!gameState) return;
    setFinalizeError(null);

    if (gameState.isFinalized) {
      setShowFinalizeDialog(false);
      return;
    }

    const rid = roundIdRef.current;
    if (rid) {
      if (!isAuthenticated) {
        // State 4 (BUG FIX): server-backed round but auth is temporarily unavailable.
        // DO NOT fall through to local/guest finalize — that would produce a
        // finalized local state with no server record, stranding the round forever.
        // Block and surface an actionable error; the queue + flush will handle the
        // remaining scores once auth returns.
        setFinalizeError(
          'Your account connection was interrupted. Your scores are safe — sign back in to finalize.'
        );
        return;
      }
      // State 1: normal server finalize
      setIsFinalizing(true);
      // Flush any pending score syncs before finalizing so hole 18 (or any
      // late-arriving score) is in the database before the server computes settlements.
      await flushQueuedSyncs(user?.id);
      const result = await finalizeRoundOnServer(rid);
      setIsFinalizing(false);
      if (result.ok) {
        const isTied = engine.settleGame(gameState).totalAmountForA === 0;
        const finalized = engine.finalizeRound(gameState);
        setGameState(finalized);
        setShowFinalizeDialog(false);
        queryClient.invalidateQueries({ queryKey: ['/api/dashboard/pending'] });
        queryClient.invalidateQueries({ queryKey: ['/api/rounds', rid, 'lifecycle'] });
        if (isTied) {
          toast({ title: 'Match is even — pushed to ledger. No money changes hands.', duration: 4000 });
        }
      } else {
        setFinalizeError(result.error);
      }
    } else if (isAuthenticated) {
      // State 3: signed in but round never reached the server — block entirely.
      // Do not finalize, do not clear localStorage. The dialog UI shows a retry path.
      return;
    } else {
      // State 2: genuine guest (not signed in) — local finalize allowed after explicit checkbox.
      const finalized = engine.finalizeRound(gameState);
      setGameState(finalized);
      setShowFinalizeDialog(false);
    }
  };

  const handleEditScores = async () => {
    if (!gameState) return;
    const rid = roundIdRef.current;
    if (rid && isAuthenticated) {
      const result = await unfinalizeRoundOnServer(rid);
      if (!result.ok) {
        setFinalizeError(result.error);
        return;
      }
    }
    const unfinalized = engine.unfinalizeRound(gameState);
    setGameState(unfinalized);
    setReviewBannerDismissed(false);
    setFinalizeError(null);
    queryClient.invalidateQueries({ queryKey: ['/api/rounds', rid] });
    queryClient.invalidateQueries({ queryKey: ['/api/rounds', rid, 'lifecycle'] });
    queryClient.invalidateQueries({ queryKey: ['/api/rounds', rid, 'presses'] });
    queryClient.invalidateQueries({ queryKey: ['/api/dashboard/pending'] });
  };

  const handleCancelFinalize = () => {
    setShowFinalizeDialog(false);
    setFinalizeError(null);
  };

  const activeHole = useMemo(() => {
    if (!gameState) return null;
    const roundConfig = extractRoundConfig(gameState);
    const scoreInputs = extractScoreInputs(gameState);
    const manualEvents = extractManualGameEvents(gameState);
    const { activeHole: ah } = engine.rebuildRoundState({
      roundConfig,
      scores: scoreInputs,
      manualGameEvents: manualEvents,
    });
    return ah;
  }, [gameState, engine]);

  const handleResumeScoring = () => {
    if (activeHole !== null) {
      setSelectedHole(activeHole);
      setShowHoleDetail(true);
    }
  };

  // ── ALL HOOKS MUST BE DECLARED ABOVE THIS LINE ──────────────────────────────
  // Conditional returns below are safe because every hook has already been called.

  if (bootStatus === 'booting') {
    if (bootHangDetected) {
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center px-6" data-testid="boot-hang-recovery">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 max-w-sm w-full text-center">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <WifiOff className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Slow connection</h2>
            <p className="text-sm text-gray-500 mb-6">
              Still connecting — your round and data are safe.
            </p>
            <div className="space-y-3">
              {liveRoundRecoveryUrl ? (
                <>
                  <button
                    onClick={handleOpenRoundNow}
                    className="w-full bg-[#064E3B] text-white font-bold py-3 px-4 rounded-xl active:bg-emerald-800 transition-colors"
                    data-testid="button-open-round-now"
                  >
                    Open Round Now
                  </button>
                  <button
                    onClick={handleCopyRoundLink}
                    className="w-full bg-white border border-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-xl active:bg-gray-50 transition-colors"
                    data-testid="button-copy-round-link"
                  >
                    Copy Round Link
                  </button>
                  <button
                    onClick={handleKeepWaiting}
                    className="w-full bg-white border border-gray-200 text-gray-500 font-semibold py-3 px-4 rounded-xl active:bg-gray-50 transition-colors"
                    data-testid="button-keep-waiting"
                  >
                    Keep Waiting
                  </button>
                </>
              ) : (
                <button
                  onClick={handleKeepWaiting}
                  className="w-full bg-[#064E3B] text-white font-bold py-3 px-4 rounded-xl active:bg-emerald-800 transition-colors"
                  data-testid="button-keep-waiting"
                >
                  Keep Waiting
                </button>
              )}
              <button
                onClick={handleResetLocalAppData}
                className="w-full text-xs text-gray-400 py-2 underline underline-offset-2"
                data-testid="button-reset-local-app-data"
              >
                Reset local app data
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" data-testid="boot-loading">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#064E3B] mx-auto" />
          <p className="text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (bootStatus === 'prompt_resume') {
    const resumeAllPlayers = gameState?.teams.flatMap(t => t.players) ?? [];
    const resumePlayerNames = resumeAllPlayers.map(p => p.name).join(', ');
    const resumeHolesScored = gameState
      ? new Set(gameState.scores.filter(s => s.grossScore !== null).map(s => s.holeNumber)).size
      : 0;
    const resumeTotalFailed = failedSyncHoles.length + failedPressSyncs.length + failedDeleteSyncs.length;
    const resumeIsServerBacked = !!(roundIdRef.current && isAuthenticated);
    const resumeHasUnsyncedData = resumeIsServerBacked && (pendingSyncs > 0 || resumeTotalFailed > 0);
    const resumeIsFullySynced = resumeIsServerBacked && pendingSyncs === 0 && resumeTotalFailed === 0;

    let syncStatusLabel: string;
    let syncStatusColor: string;
    if (!resumeIsServerBacked) {
      syncStatusLabel = 'Not saved to server — discarding permanently loses all local data.';
      syncStatusColor = 'text-red-600';
    } else if (resumeHasUnsyncedData) {
      const parts: string[] = [];
      if (pendingSyncs > 0) parts.push(`${pendingSyncs} score${pendingSyncs !== 1 ? 's' : ''} pending`);
      if (resumeTotalFailed > 0) parts.push(`${resumeTotalFailed} failed sync${resumeTotalFailed !== 1 ? 's' : ''}`);
      syncStatusLabel = `${parts.join(', ')} — unsynced data will be permanently lost if discarded.`;
      syncStatusColor = 'text-amber-600';
    } else {
      syncStatusLabel = 'All data confirmed by server. The server round will not be affected.';
      syncStatusColor = 'text-emerald-700';
    }

    return (
      <>
        <div className="min-h-screen bg-gray-100 flex items-center justify-center px-6" data-testid="prompt-resume">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 max-w-sm w-full text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Play className="w-7 h-7 text-emerald-700" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">You have an unfinished round.</h2>
            <p className="text-sm text-gray-500 mb-6">
              {gameState?.courseDataId || 'Your match'} is still in progress. Would you like to pick up where you left off?
            </p>
            <div className="space-y-3">
              <button
                onClick={handleResumeMatch}
                className="w-full bg-[#064E3B] text-white font-bold py-3 px-4 rounded-xl active:bg-emerald-800 transition-colors"
                data-testid="button-resume-match"
              >
                Resume Match
              </button>
              <button
                onClick={handleDiscardMatch}
                className="w-full text-gray-500 font-medium py-3 px-4 rounded-xl active:bg-gray-100 transition-colors"
                data-testid="button-discard-match"
              >
                Discard &amp; Start New
              </button>
            </div>
          </div>
        </div>

        <Dialog open={showDiscardConfirmDialog} onOpenChange={(open) => { if (!open) setShowDiscardConfirmDialog(false); }}>
          <DialogContent className="sm:max-w-md" data-testid="dialog-discard-confirm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Discard this round?
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-3 text-left">
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Course</span>
                      <span className="font-medium text-gray-900 text-right max-w-[60%] truncate" data-testid="text-discard-course">
                        {gameState?.courseDataId || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Players</span>
                      <span className="font-medium text-gray-900 text-right max-w-[60%]" data-testid="text-discard-players">
                        {resumePlayerNames || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Holes entered</span>
                      <span className="font-medium text-gray-900" data-testid="text-discard-holes">
                        {resumeHolesScored} of 18
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Finalized</span>
                      <span className="font-medium text-gray-900" data-testid="text-discard-finalized">
                        {gameState?.isFinalized ? 'Yes' : 'No'}
                      </span>
                    </div>
                    {resumeIsServerBacked && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Pending syncs</span>
                          <span className="font-medium text-gray-900" data-testid="text-discard-pending-syncs">
                            {pendingSyncs}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Failed syncs</span>
                          <span className="font-medium text-gray-900" data-testid="text-discard-failed-syncs">
                            {resumeTotalFailed}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <p className={`text-xs font-medium ${syncStatusColor}`} data-testid="text-discard-sync-status">
                    {syncStatusLabel}
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setShowDiscardConfirmDialog(false)}
                data-testid="button-discard-cancel"
              >
                Keep This Round
              </Button>
              <Button
                variant="destructive"
                onClick={executeDiscardMatch}
                data-testid="button-discard-confirm"
              >
                Discard Local &amp; Start New
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <BottomNav />
      </>
    );
  }

  if (bootStatus === 'network_error') {
    return (
      <>
        <div className="min-h-screen bg-gray-100 flex items-center justify-center px-6" data-testid="network-error">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 max-w-sm w-full text-center">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <WifiOff className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">We couldn't verify your last round was saved.</h2>
            <p className="text-sm text-gray-500 mb-6">
              Check your connection and try again, or continue to setup.
            </p>
            <div className="space-y-3">
              <button
                onClick={handleNetworkRetry}
                className="w-full bg-[#064E3B] text-white font-bold py-3 px-4 rounded-xl active:bg-emerald-800 transition-colors"
                data-testid="button-network-retry"
              >
                Try Again
              </button>
              <button
                onClick={handleNetworkContinue}
                className="w-full text-gray-500 font-medium py-3 px-4 rounded-xl active:bg-gray-100 transition-colors"
                data-testid="button-network-continue"
              >
                Continue to Setup
              </button>
            </div>
          </div>
        </div>
        <BottomNav />
      </>
    );
  }

  if (isResuming) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" data-testid="resume-loading">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#064E3B] mx-auto" />
          <p className="text-sm font-medium text-gray-600">Resuming round...</p>
          <p className="text-xs text-gray-400">Syncing data and rebuilding state</p>
        </div>
      </div>
    );
  }

  if (!gameState || viewMode === 'setup') {
    return (
      <>
        <SetupScreen
          onStartRound={handleStartRound}
          existingGameState={viewMode === 'setup' ? gameState : null}
          onResumeRound={handleResumeRound}
          onCancelEdit={gameState ? handleCancelEdit : undefined}
          onLoadRound={handleLoadRound}
        />
        <BottomNav />
      </>
    );
  }

  // Gate B: recovery_required screen — local says finalized, server says active.
  // Rendered before the normal finalized/ResultsScreen path so the coach screen
  // intercepts the finalized state before ResultsScreen can show wrong data.
  if (isRecoveryRequired && roundIdRef.current) {
    // Gate B: "Review Local Scores" sub-view — show ResultsScreen with the
    // reviewLocalScores flag so no server data is fetched and settlement is
    // suppressed. isRecoveryRequired stays true so "Back" always works.
    if (isReviewLocalScoresOpen && gameState) {
      return (
        <>
          <ResultsScreen
            gameState={gameState}
            reviewLocalScores={true}
            onReturnToRecovery={() => setIsReviewLocalScoresOpen(false)}
            readOnly={true}
          />
          <BottomNav />
        </>
      );
    }

    const rid = roundIdRef.current;
    const handleRecoveryRetrySyncAndFinalize = async () => {
      if (!isAuthenticated) {
        setFinalizeError('Sign back in to retry finalization.');
        return;
      }
      setFinalizeError(null);
      setIsFinalizing(true);
      // Gate B: flush the queue first. Only proceed to finalize if all pending
      // entries were accepted by the server. If any remain (network failure,
      // 4xx), surface an error and stay on the recovery screen — never call
      // finalize with unsynced data still in the queue.
      const { queuedRemaining } = await flushQueuedSyncs(user?.id);
      // Also check the failed-syncs queue: entries that exhausted all retry
      // attempts are moved there rather than staying in the active queue.
      // Finalization must not proceed while any action for this round remains
      // unsynced in either queue.
      if (queuedRemaining > 0 || hasSyncIssuesForRound(rid)) {
        setFinalizeError(
          'Score updates could not be synced — please retry or check your connection.'
        );
        setIsFinalizing(false);
        return;
      }
      const result = await finalizeRoundOnServer(rid);
      setIsFinalizing(false);
      if (result.ok) {
        const finalized = engine.finalizeRound(gameState!);
        setGameState(finalized);
        setIsRecoveryRequired(false);
        queryClient.invalidateQueries({ queryKey: ['/api/dashboard/pending'] });
        queryClient.invalidateQueries({ queryKey: ['/api/rounds', rid, 'lifecycle'] });
      } else {
        setFinalizeError(result.error ?? 'Finalization failed. Your scores are preserved — please try again.');
      }
    };
    const handleRecoveryResumeEditing = () => {
      if (!gameState) return;
      const unfinalized = engine.unfinalizeRound(gameState);
      setGameState(unfinalized);
      setIsRecoveryRequired(false);
    };
    const handleRecoveryExportBackup = () => {
      if (!gameState) return;
      const json = JSON.stringify(gameState, null, 2);
      const shortId = rid.slice(0, 8);
      const date = new Date().toISOString().slice(0, 10);
      const filename = `doublepress-round-${shortId}-backup-${date}.json`;
      try {
        navigator.clipboard.writeText(json);
        toast({ title: `Backup copied to clipboard (${filename})`, duration: 4000 });
      } catch {
        toast({ title: 'Could not copy backup — check browser permissions.', duration: 4000 });
      }
    };

    return (
      <>
        <div className="min-h-screen bg-gray-100" data-testid="banner-recovery-required">
          <header className="bg-amber-600 text-white px-4 pb-4 sticky top-0 z-50 safe-area-top">
            <div className="flex items-center gap-3 max-w-4xl mx-auto pt-2">
              <AlertTriangle className="w-6 h-6 text-white flex-shrink-0" />
              <div>
                <h1 className="font-bold text-lg leading-tight">Round may not have saved correctly</h1>
                <p className="text-xs text-amber-100 mt-0.5">Your local scores are preserved</p>
              </div>
            </div>
          </header>

          <main className="px-4 py-4 pb-24 max-w-lg mx-auto space-y-4">
            {/* Status summary */}
            <div className="bg-white rounded-xl border border-amber-200 p-4 space-y-2">
              <p className="text-sm font-semibold text-gray-800">
                {(gameState as any)?.courseData?.name ?? (gameState as any)?.courseDataId ?? 'Golf Course'} · {new Date().toLocaleDateString()}
              </p>
              <div className="flex flex-col gap-1 text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <span className="w-28 font-medium text-gray-500">Server status:</span>
                  <span className="font-semibold text-amber-700">Active (not finalized)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-28 font-medium text-gray-500">Local status:</span>
                  <span className="font-semibold text-gray-700">Finalized</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 pt-1">
                The previous finalization attempt did not reach the server. Your scores are intact — use one of the options below to resolve this.
              </p>
            </div>

            {finalizeError && (
              <p className="text-sm text-red-600 px-1" data-testid="text-recovery-error">{finalizeError}</p>
            )}

            {/* Primary recovery action */}
            <button
              onClick={handleRecoveryRetrySyncAndFinalize}
              disabled={isFinalizing || !isAuthenticated}
              className="w-full bg-[#064E3B] text-white font-semibold rounded-xl py-3 px-4 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="button-recovery-retry-finalize"
            >
              {isFinalizing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4" strokeDashoffset="10" /></svg>
                  Syncing &amp; Finalizing…
                </>
              ) : !isAuthenticated ? (
                'Sign In to Retry Finalization'
              ) : (
                'Retry Sync & Finalize'
              )}
            </button>

            {/* Review local scores — shows ResultsScreen with reviewLocalScores=true.
                isRecoveryRequired stays true so the user can return here. */}
            <button
              onClick={() => setIsReviewLocalScoresOpen(true)}
              className="w-full bg-white border border-gray-300 text-gray-700 font-semibold rounded-xl py-3 px-4 text-sm"
              data-testid="button-recovery-review-scores"
            >
              Review Local Scores
            </button>

            {/* Resume editing — unfinalizes local state, returns to scorecard */}
            <button
              onClick={handleRecoveryResumeEditing}
              disabled={!isAuthenticated}
              className="w-full bg-white border border-gray-300 text-gray-700 font-semibold rounded-xl py-3 px-4 text-sm disabled:opacity-50"
              data-testid="button-recovery-resume-editing"
            >
              Resume Editing
            </button>

            {/* Export backup */}
            <button
              onClick={handleRecoveryExportBackup}
              className="w-full bg-white border border-gray-300 text-gray-700 font-medium rounded-xl py-3 px-4 text-sm"
              data-testid="button-recovery-export-backup"
            >
              Export Backup (Copy JSON)
            </button>

            {/* Abandon — disabled until server endpoint is implemented */}
            <button
              disabled
              className="w-full bg-white border border-gray-200 text-gray-400 font-medium rounded-xl py-3 px-4 text-sm cursor-not-allowed"
              data-testid="button-recovery-abandon"
              title="Requires a future update"
            >
              Abandon Round — Requires a future update
            </button>

            {/* Contact support — copyable round ID */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2" data-testid="recovery-contact-support">
              <p className="text-sm font-medium text-gray-700">Contact Support</p>
              <p className="text-xs text-gray-500">Copy your Round ID and include it when contacting support.</p>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 text-xs bg-gray-100 border border-gray-200 rounded px-2 py-1.5 font-mono text-gray-800 select-all break-all" data-testid="text-recovery-round-id">
                  {rid}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(rid);
                    toast({ title: 'Round ID copied', duration: 2000 });
                  }}
                  className="text-xs font-semibold text-emerald-700 underline underline-offset-2 whitespace-nowrap flex-shrink-0"
                  data-testid="button-copy-recovery-round-id"
                >
                  Copy
                </button>
              </div>
            </div>
          </main>
        </div>
        <BottomNav />
      </>
    );
  }

  if (gameState.isFinalized) {
    return (
      <>
        <ResultsScreen 
          gameState={gameState} 
          onEditScores={handleEditScores}
          onNewRound={handleNewRound}
          editScoresError={finalizeError}
          readOnly={!roundOwnerId || roundOwnerId !== user?.id}
        />
        <BottomNav />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white border-b border-gray-200 px-4 pb-2 sticky top-0 z-50 safe-area-top">
        <div className="flex items-center max-w-4xl mx-auto w-full">
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToSetup}
              data-testid="button-back"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            {isAuthenticated && !!gameState?.roundId && roundOwnerId === user?.id ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleHeaderShare}
                disabled={headerShareLoading}
                className="gap-1.5 text-[#064E3B] font-medium min-w-0 shrink"
                data-testid="button-header-share"
              >
                {headerShareLoading
                  ? <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                  : <UserPlus className="w-4 h-4 flex-shrink-0" />}
                <span className="truncate text-sm">Send Live Scoring</span>
                {pendingSyncs > 0 && (
                  <span className="text-amber-500 flex-shrink-0" data-testid="sync-pending-indicator">•</span>
                )}
              </Button>
            ) : pendingSyncs > 0 ? (
              <span className="text-amber-500 text-xs ml-1" data-testid="sync-pending-indicator">Syncing...</span>
            ) : null}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={() => setShowBetStatus(true)}
              className="gap-2 text-base font-semibold px-5 py-2 bg-[#FACC15] hover:bg-[#EABF10] text-[#064E3B] rounded-[12px] shadow-[0_4px_12px_rgba(0,0,0,0.25)]"
              data-testid="button-bet-status"
            >
              <BarChart3 className="w-5 h-5" />
              Bet Status
            </Button>
          </div>
          <div className="flex items-center gap-2 flex-1 justify-end">
            {engine.isRoundComplete(gameState) && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowFinalizeDialog(true)}
                className="gap-2 bg-[#064E3B] hover:bg-[#053f30] text-[#FACC15]"
                data-testid="button-finalize"
              >
                <Flag className="w-4 h-4" />
                Finalize
              </Button>
            )}
            {gameState.courseDataId && !gameState.isFinalized && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefreshStrokeIndexes}
                title="Refresh stroke indexes from course database"
                data-testid="button-refresh-strokes"
                className="text-gray-400 hover:text-gray-600"
              >
                <Wrench className="w-4 h-4" />
              </Button>
            )}
            {isAuthenticated && roundOwnerId === user?.id && !!gameState?.roundId && !gameState.isFinalized && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-gray-400 hover:text-gray-600"
                    data-testid="button-scorecard-menu"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600 gap-2"
                    onClick={handleDiscardMatch}
                    data-testid="menu-item-discard"
                  >
                    <Trash2 className="w-4 h-4" />
                    Discard &amp; Start New
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <AuthButton />
          </div>
        </div>
      </header>

      {/* ── Local-only warning banner ─────────────────────────────────────────── */}
      {!roundIdRef.current && !isUpgradingLocalRound && (
        <div className="bg-red-50 border-b-2 border-red-300 px-4 py-2.5" data-testid="banner-local-round">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <ShieldAlert className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-800 font-medium leading-snug">
                {isAuthenticated && upgradeFailed
                  ? <><strong>Saving failed.</strong> Tap Retry to save this round to your account.</>
                  : <><strong>Not saving.</strong> Sign in so this round appears in your ledger.</>
                }
              </p>
            </div>
            {isAuthenticated && upgradeFailed ? (
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={handleExportBackup}
                  className="text-xs font-medium text-red-500 underline underline-offset-2 whitespace-nowrap"
                  data-testid="button-local-banner-export"
                  title="Export all local round data as JSON for safekeeping before retrying"
                >
                  Backup
                </button>
                <button
                  onClick={() => {
                    autoUpgradeAttemptedRef.current = false;
                    if (gameState) runUpgrade(gameState);
                  }}
                  className="text-xs font-semibold text-red-700 underline underline-offset-2 whitespace-nowrap"
                  data-testid="button-local-banner-retry"
                >
                  Retry
                </button>
              </div>
            ) : !isAuthenticated ? (
              <button
                onClick={() => { window.location.href = '/auth?mode=signin&returnTo=/'; }}
                className="text-xs font-semibold text-red-700 underline underline-offset-2 whitespace-nowrap flex-shrink-0"
                data-testid="button-local-banner-signin"
              >
                Sign In
              </button>
            ) : null}
          </div>
        </div>
      )}
      {isUpgradingLocalRound && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2" data-testid="banner-upgrading-round">
          <div className="max-w-4xl mx-auto flex items-center gap-2">
            <Loader2 className="w-4 h-4 text-emerald-600 animate-spin flex-shrink-0" />
            <p className="text-xs text-emerald-800 font-medium">Saving round to your account…</p>
          </div>
        </div>
      )}

      {/* ── Auth-hold banner: server-backed round, auth temporarily unavailable ── */}
      {!!roundIdRef.current && !isAuthenticated && !!gameState && !gameState.isFinalized && (
        <div className="bg-amber-50 border-b-2 border-amber-400 px-4 py-2.5" data-testid="banner-auth-hold">
          <div className="max-w-4xl mx-auto flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-900 font-medium leading-snug">
                  {/* Three distinct states:
                      1. Clerk permanently failed to load (clerkTimedOut && !isClerkLoaded) —
                         do NOT say "session expired"; Clerk never confirmed the session ended.
                         Show a recovery path instead.
                      2. Clerk loaded + session explicitly gone (isClerkLoaded && !isClerkSignedIn) —
                         session IS confirmed expired; show Sign In.
                      3. Everything else — transient drop; passive message only. */}
                  {clerkTimedOut && !isClerkLoaded
                    ? clerkTimedOutBannerState === 'retry_failed'
                      ? <><strong>Account connection could not be loaded.</strong> Your scores are saved on this device. Copy your Round ID below and contact support if needed.</>
                      : <><strong>Account connection could not be loaded.</strong> Your scores are saved on this device.</>
                    : isClerkLoaded && !isClerkSignedIn
                      ? <><strong>Session expired.</strong> Scores are queued safely — sign back in to sync and finalize.</>
                      : <><strong>Connection interrupted.</strong> Scores are saved on this device. Syncing will resume automatically when your connection returns.</>
                  }
                </p>
              </div>

              {/* Action button — varies by state */}
              {clerkTimedOut && !isClerkLoaded
                ? clerkTimedOutBannerState === 'retrying'
                  ? <span className="text-xs text-amber-700 whitespace-nowrap flex-shrink-0">Retrying…</span>
                  : clerkTimedOutBannerState === 'actionable'
                    ? (
                      <button
                        onClick={handleClerkRetry}
                        className="text-xs font-semibold text-amber-800 underline underline-offset-2 whitespace-nowrap flex-shrink-0"
                        data-testid="button-clerk-retry"
                      >
                        Check Connection Again
                      </button>
                    )
                    : null // retry_failed — actions rendered in the row below
                : isClerkLoaded && !isClerkSignedIn && !recoveryAttempted
                  ? (
                    <button
                      onClick={() => {
                        setRecoveryAttempted(true);
                        window.location.href = '/auth?mode=signin&returnTo=/';
                      }}
                      className="text-xs font-semibold text-amber-800 underline underline-offset-2 whitespace-nowrap flex-shrink-0"
                      data-testid="button-auth-hold-signin"
                    >
                      Sign In
                    </button>
                  )
                  : null
              }
            </div>

            {/* Secondary row — Clerk-timeout state only.
                "Open Sign-In" has been intentionally removed from this sub-state.
                When clerkTimedOut && !isClerkLoaded, Clerk has not loaded in this
                session — it was likely blocked by a firewall or network issue.
                Navigating to /auth would open the same broken session context;
                Clerk would also fail to load there, leaving the user on a blank
                page with no recovery path. returnTo=/ would return to Home, not
                the Scorecard, and all local queue/state context would be lost
                (requires the active-round prompt to find the round again).
                The reliable recovery path in this sub-state is:
                  1. Check Connection Again (re-fetches /api/auth/user to recheck
                     the server-side session; may succeed if the Clerk loading
                     block was transient and Clerk has since finished loading in
                     the background — this does NOT reinitialize ClerkProvider)
                  2. Copy Round ID → contact support if retry fails
                Do not add a navigation button here unless it can be proven to
                complete its stated action when Clerk is unloaded. */}
            {clerkTimedOut && !isClerkLoaded && clerkTimedOutBannerState === 'retry_failed' && !!roundIdRef.current && (
              <div className="flex items-center gap-3 pl-6 flex-wrap">
                <code className="text-xs bg-amber-100 border border-amber-300 rounded px-2 py-0.5 font-mono text-amber-900 select-all" data-testid="text-clerk-retry-round-id">
                  {roundIdRef.current}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(roundIdRef.current ?? '')}
                  className="text-xs font-semibold text-amber-700 underline underline-offset-2 whitespace-nowrap"
                  data-testid="button-copy-clerk-retry-round-id"
                >
                  Copy ID
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Round-missing banner: server returned 404/410 for this round ── */}
      {roundMissing && !!roundIdRef.current && (
        <div className="bg-red-50 border-b-2 border-red-500 px-4 py-3" data-testid="banner-round-missing">
          <div className="max-w-4xl mx-auto flex flex-col gap-2">
            <div className="flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-900 font-medium leading-snug">
                <strong>Round not found on server.</strong> Your local scores are preserved. Do not start a replacement round. Copy the Round ID and contact support.
              </p>
            </div>
            <div className="flex items-center gap-2 pl-6">
              <code className="text-xs bg-red-100 border border-red-300 rounded px-2 py-0.5 font-mono text-red-900 select-all" data-testid="text-missing-round-id">
                {roundIdRef.current}
              </code>
              <button
                onClick={() => navigator.clipboard.writeText(roundIdRef.current ?? '')}
                className="text-xs font-semibold text-red-700 underline underline-offset-2 whitespace-nowrap"
                data-testid="button-copy-round-id"
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Commissioner-mismatch banner: queue was created by a different user ── */}
      {showOwnerMismatch && (
        <div className="bg-red-50 border-b-2 border-red-400 px-4 py-2.5" data-testid="banner-owner-mismatch">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <ShieldAlert className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-900 font-medium leading-snug">
                <strong>Wrong account.</strong> This round was scored by a different account. Sign in as the original scorer to sync.
              </p>
            </div>
            <button
              onClick={() => setShowOwnerMismatch(false)}
              className="text-xs font-semibold text-red-700 underline underline-offset-2 whitespace-nowrap flex-shrink-0"
              data-testid="button-owner-mismatch-dismiss"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {lifecycle && lifecycle.status === 'pending_finalization' && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2" data-testid="banner-pending-finalization">
          <div className="max-w-4xl mx-auto flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-800">This round isn't finalized — payouts and season stats aren't recorded yet.</p>
          </div>
        </div>
      )}
      {lifecycle && lifecycle.status === 'abandoned' && (
        <div className="bg-gray-100 border-b border-gray-300 px-4 py-2" data-testid="banner-abandoned">
          <div className="max-w-4xl mx-auto flex items-center gap-2">
            <Ban className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <p className="text-xs text-gray-600">This round was abandoned and won't count toward season stats.</p>
          </div>
        </div>
      )}

      {correctionMode && (
        <div className="bg-amber-50 border-b-2 border-amber-300 px-4 py-3" data-testid="banner-correction-mode">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Eraser className="w-5 h-5 text-amber-700 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-amber-900">Correction Mode</p>
                  <p className="text-xs text-amber-700">Tap the hole where the mistake began. Scores from that hole forward will be cleared, and press history will rebuild automatically.</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelCorrection}
                className="text-amber-900 border-amber-400 bg-white hover:bg-amber-100 font-semibold flex-shrink-0 gap-1"
                data-testid="button-cancel-correction"
              >
                <X className="w-3.5 h-3.5" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <main
        className="px-4 py-4 max-w-4xl mx-auto"
        style={{
          paddingBottom: hole18ScenariosData || activeHole !== null
            ? 'calc(8rem + env(safe-area-inset-bottom, 0px))'
            : showReviewBanner
            ? 'calc(12rem + env(safe-area-inset-bottom, 0px))'
            : 'calc(6rem + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="overflow-x-auto">
          <ScorecardShell
            gameState={gameState}
            onHoleHeaderClick={(holeNumber) => { if (gameState.isFinalized) return; if (showFinalizeDialog) setShowFinalizeDialog(false); setSelectedHole(holeNumber); setShowHoleDetail(true); }}
            correctionMode={correctionMode}
            correctionFromHole={correctionFromHole}
            onCorrectionHoleSelect={handleCorrectionHoleSelect}
          />
          <PressRows gameState={gameState} onAddPress={handleAddPress} onPressExistingPress={handlePressExistingPress} selectedHole={selectedHole} />
        </div>
      </main>

      {activeHole !== null && !showHoleDetail && !showBetStatus && !showFinalizeDialog && !correctionMode && (
        <div
          className="fixed left-0 right-0 z-30 flex justify-center px-4"
          style={{ bottom: hole18ScenariosData
            /* Scenarios panel max height (60dvh content + ~3rem header) + nav + safe-area + gap.
               This guarantees the button is above the panel in any expanded/collapsed state. */
            ? 'calc(var(--bottom-nav-h) + env(safe-area-inset-bottom, 0px) + 60dvh + 4rem)'
            : 'calc(var(--bottom-nav-h) + env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
          data-testid="resume-scoring-container"
        >
          <Button
            onClick={handleResumeScoring}
            className="gap-2 px-6 py-3 text-base font-bold bg-[#2F7A5F] hover:bg-[#256B52] text-white rounded-[12px] min-h-[48px] shadow-[0_4px_12px_rgba(0,0,0,0.25)]"
            data-testid="button-resume-scoring"
          >
            <Play className="w-5 h-5 fill-current" />
            Resume Scoring — Hole {activeHole}
          </Button>
        </div>
      )}

      {failedSyncHoles.length > 0 && !gameState?.isFinalized && roundIdRef.current && (
        <div
          className="fixed left-0 right-0 z-41 px-4 pb-2"
          style={{ bottom: 'calc(var(--bottom-nav-h) + env(safe-area-inset-bottom, 0px))' }}
          data-testid="banner-sync-failed"
        >
          <div className="max-w-4xl mx-auto bg-red-50 border border-red-300 rounded-xl shadow-lg p-3">
            <div className="flex items-start gap-3">
              <WifiOff className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-red-800">
                  Score sync failed —{' '}
                  {failedSyncHoles.length === 1
                    ? `Hole ${failedSyncHoles[0]}`
                    : `Holes ${failedSyncHoles.join(', ')}`}{' '}
                  didn't reach the server
                </p>
                <p className="text-xs text-red-700 mt-0.5">
                  Your scores are saved on this device. Tap Retry to re-send, or tap the hole to review.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-2 justify-end">
              {failedSyncHoles.map(hole => (
                <Button
                  key={hole}
                  variant="outline"
                  size="sm"
                  className="text-xs h-7 border-red-300 text-red-700 hover:bg-red-100"
                  data-testid={`button-sync-retry-hole-${hole}`}
                  onClick={() => {
                    const rid = roundIdRef.current;
                    if (rid) requeueFailedHoleSync(rid, hole);
                  }}
                >
                  Retry Hole {hole}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      {failedPressSyncs.length > 0 && !gameState?.isFinalized && roundIdRef.current && (
        <div
          className="fixed left-0 right-0 z-41 px-4 pb-2"
          style={{ bottom: 'calc(var(--bottom-nav-h) + env(safe-area-inset-bottom, 0px))' }}
          data-testid="banner-press-sync-failed"
        >
          <div className="max-w-4xl mx-auto bg-red-50 border border-red-300 rounded-xl shadow-lg p-3">
            <div className="flex items-start gap-3">
              <WifiOff className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-red-800">
                  {failedPressSyncs.length === 1
                    ? `${failedPressSyncs[0].betType} press didn't reach the server`
                    : `${failedPressSyncs.length} presses didn't reach the server`}
                </p>
                <p className="text-xs text-red-700 mt-0.5">
                  This press is saved on this device but not yet counted toward the settlement. Tap Retry to re-send. You can't finalize until it syncs.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-2 justify-end flex-wrap">
              {failedPressSyncs.map(p => (
                <Button
                  key={p.clientId}
                  variant="outline"
                  size="sm"
                  className="text-xs h-7 border-red-300 text-red-700 hover:bg-red-100"
                  data-testid={`button-sync-retry-press-${p.clientId}`}
                  onClick={() => {
                    const rid = roundIdRef.current;
                    if (rid) requeueFailedPressSync(rid, p.clientId);
                  }}
                >
                  Retry {p.betType} Press
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      {failedDeleteSyncs.length > 0 && !gameState?.isFinalized && roundIdRef.current && (
        <div
          className="fixed left-0 right-0 z-41 px-4 pb-2"
          style={{ bottom: 'calc(var(--bottom-nav-h) + env(safe-area-inset-bottom, 0px))' }}
          data-testid="banner-delete-press-sync-failed"
        >
          <div className="max-w-4xl mx-auto bg-red-50 border border-red-300 rounded-xl shadow-lg p-3">
            <div className="flex items-start gap-3">
              <WifiOff className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-red-800">
                  {failedDeleteSyncs.length === 1
                    ? `A press deletion didn't reach the server`
                    : `${failedDeleteSyncs.length} press deletions didn't reach the server`}
                </p>
                <p className="text-xs text-red-700 mt-0.5">
                  The deletion is saved on this device but the server still counts this press toward the settlement. Tap Retry to re-send. You can't finalize until it syncs.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-2 justify-end flex-wrap">
              {failedDeleteSyncs.map(d => (
                <Button
                  key={d.pressId}
                  variant="outline"
                  size="sm"
                  className="text-xs h-7 border-red-300 text-red-700 hover:bg-red-100"
                  data-testid={`button-sync-retry-delete-press-${d.pressId}`}
                  onClick={() => {
                    const rid = roundIdRef.current;
                    if (rid) requeueFailedDeleteSync(rid, d.pressId);
                  }}
                >
                  Retry Delete Press
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showReviewBanner && (
        <div
          className="fixed left-0 right-0 z-40 px-4 pb-2"
          style={{ bottom: 'calc(var(--bottom-nav-h) + env(safe-area-inset-bottom, 0px))' }}
          data-testid="banner-review-round"
        >
          <div className="max-w-4xl mx-auto bg-white border border-emerald-200 rounded-xl shadow-lg p-3">
            <div className="flex items-start gap-3">
              <Flag className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-gray-700 flex-1">
                Your round has ended. Please review your scores before finalizing the round. Tap any hole to make changes.
              </p>
              <button
                onClick={() => setReviewBannerDismissed(true)}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                data-testid="button-dismiss-review-banner"
                aria-label="Dismiss review banner"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-2 mt-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReviewBannerDismissed(true)}
                className="text-xs h-8"
                data-testid="button-review-scorecard"
              >
                Review Scorecard
              </Button>
              <Button
                size="sm"
                onClick={() => { setReviewBannerDismissed(true); setShowFinalizeDialog(true); }}
                className="bg-[#064E3B] hover:bg-[#053f30] text-[#FACC15] text-xs h-8"
                data-testid="button-finalize-from-banner"
              >
                <Flag className="w-3 h-3 mr-1" />
                Finalize Round
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showFinalizeDialog} onOpenChange={setShowFinalizeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-[#064E3B]" />
              Finalize Round?
            </DialogTitle>
            <DialogDescription>
              Have you reviewed all your scores before you finalize the round?
            </DialogDescription>
            <p className="text-xs text-muted-foreground italic mt-1">Finalizing locks in payouts and updates your season record.</p>
          </DialogHeader>
          {!roundIdRef.current && isAuthenticated && (
            // State 3: signed in but save failed — block finalize, show retry
            <div className="space-y-3">
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5">
                <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-900 leading-snug">
                  <strong>This round has not saved to your account yet.</strong> Retry saving before finalizing — your scores are safe and will not be deleted.
                </p>
              </div>
              <button
                className="text-sm font-semibold text-[#064E3B] underline underline-offset-2 disabled:opacity-50"
                disabled={isUpgradingLocalRound}
                onClick={() => {
                  setShowFinalizeDialog(false);
                  autoUpgradeAttemptedRef.current = false;
                  if (gameState) runUpgrade(gameState);
                }}
                data-testid="button-finalize-retry-save"
              >
                {isUpgradingLocalRound ? 'Saving…' : 'Retry Save →'}
              </button>
            </div>
          )}
          {!roundIdRef.current && !isAuthenticated && (
            // State 2: genuine guest — allow local finalize with explicit warning
            <div className="space-y-3">
              <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-3 py-2.5">
                <ShieldAlert className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                <p className="text-sm text-red-800 leading-snug">
                  <strong>This round is not saved to any account.</strong> Finalizing will permanently delete it — it won't appear in anyone's ledger. Sign in first to keep the record.
                </p>
              </div>
              <button
                className="text-sm font-semibold text-[#064E3B] underline underline-offset-2"
                onClick={() => { setShowFinalizeDialog(false); window.location.href = '/auth?mode=signin&returnTo=/'; }}
                data-testid="button-finalize-signin"
              >
                Sign in to save this round →
              </button>
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="local-finalize-confirm"
                  checked={localFinalizeConfirmed}
                  onCheckedChange={(v) => setLocalFinalizeConfirmed(!!v)}
                  data-testid="checkbox-local-finalize-confirm"
                />
                <label htmlFor="local-finalize-confirm" className="text-sm text-gray-700 leading-snug cursor-pointer">
                  I understand this round won't be saved anywhere
                </label>
              </div>
            </div>
          )}
          {failedSyncHoles.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-3 py-2.5">
                <WifiOff className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-red-800 leading-snug">
                    {failedSyncHoles.length === 1
                      ? `Hole ${failedSyncHoles[0]} score didn't reach the server`
                      : `Holes ${failedSyncHoles.join(', ')} scores didn't reach the server`}
                  </p>
                  <p className="text-xs text-red-700 mt-0.5 leading-snug">
                    Your scores are saved on this device. Tap Retry to re-send without re-entering, then finalize once the sync clears.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {failedSyncHoles.map(hole => (
                  <button
                    key={hole}
                    className="text-sm font-semibold text-[#064E3B] underline underline-offset-2"
                    data-testid={`button-finalize-retry-hole-${hole}`}
                    onClick={() => {
                      const rid = roundIdRef.current;
                      if (rid) requeueFailedHoleSync(rid, hole);
                    }}
                  >
                    Retry Hole {hole} →
                  </button>
                ))}
              </div>
            </div>
          )}
          {(failedPressSyncs.length > 0 || pendingPressCount > 0) && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-3 py-2.5">
                <WifiOff className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-red-800 leading-snug">
                    {failedPressSyncs.length > 0
                      ? (failedPressSyncs.length === 1
                        ? `${failedPressSyncs[0].betType} press didn't reach the server`
                        : `${failedPressSyncs.length} presses didn't reach the server`)
                      : 'A press is still syncing'}
                  </p>
                  <p className="text-xs text-red-700 mt-0.5 leading-snug">
                    Presses affect the settlement amount, so finalize is blocked until this syncs. Tap Retry, or wait a moment for it to finish syncing.
                  </p>
                </div>
              </div>
              {failedPressSyncs.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {failedPressSyncs.map(p => (
                    <button
                      key={p.clientId}
                      className="text-sm font-semibold text-[#064E3B] underline underline-offset-2"
                      data-testid={`button-finalize-retry-press-${p.clientId}`}
                      onClick={() => {
                        const rid = roundIdRef.current;
                        if (rid) requeueFailedPressSync(rid, p.clientId);
                      }}
                    >
                      Retry {p.betType} Press →
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {failedDeleteSyncs.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-3 py-2.5">
                <WifiOff className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-red-800 leading-snug">
                    {failedDeleteSyncs.length === 1
                      ? `A press deletion didn't reach the server`
                      : `${failedDeleteSyncs.length} press deletions didn't reach the server`}
                  </p>
                  <p className="text-xs text-red-700 mt-0.5 leading-snug">
                    The server still counts this press toward the settlement. Tap Retry to re-send, then finalize once the sync clears.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {failedDeleteSyncs.map(d => (
                  <button
                    key={d.pressId}
                    className="text-sm font-semibold text-[#064E3B] underline underline-offset-2"
                    data-testid={`button-finalize-retry-delete-press-${d.pressId}`}
                    onClick={() => {
                      const rid = roundIdRef.current;
                      if (rid) requeueFailedDeleteSync(rid, d.pressId);
                    }}
                  >
                    Retry Press Deletion →
                  </button>
                ))}
              </div>
            </div>
          )}
          {finalizeError && (
            <p className="text-sm text-red-600 px-1" data-testid="text-finalize-error">{finalizeError}</p>
          )}
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => { setShowFinalizeDialog(false); setFinalizeError(null); setLocalFinalizeConfirmed(false); }}
              data-testid="button-finalize-no"
            >
              No
            </Button>
            <Button
              onClick={handleFinalizeRound}
              className="bg-[#064E3B] hover:bg-[#053f30] text-[#FACC15]"
              data-testid="button-finalize-yes"
              disabled={isFinalizing || roundMissing || (!!roundIdRef.current && !isAuthenticated) || (!roundIdRef.current && (isAuthenticated || !localFinalizeConfirmed)) || failedSyncHoles.length > 0 || failedPressSyncs.length > 0 || pendingPressCount > 0 || failedDeleteSyncs.length > 0}
            >
              {isFinalizing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Finalizing...
                </>
              ) : (
                'Yes, Finalize'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {hole18ScenariosData && (
        <Hole18Scenarios data={hole18ScenariosData} />
      )}

      {showBetStatus && (
        <BetStatusModal
          gameState={gameState}
          onClose={() => setShowBetStatus(false)}
          onDeletePress={gameState.gameMode === 'OnDemandPress' && !gameState.isFinalized ? handleDeletePress : undefined}
        />
      )}

      {showHoleDetail && selectedHole !== null && (
        <HoleDetailModal
          gameState={gameState}
          holeNumber={selectedHole}
          onClose={() => setShowHoleDetail(false)}
          onBulkScoreChange={handleBulkScoreChange}
          onHoleChange={(h) => setSelectedHole(h)}
          onClearHole={handleClearHole}
          onCorrectEarlierHole={handleCorrectEarlierHole}
          onRequestPress={handleAddPress}
          isCommish={true}
        />
      )}

      <ConfirmPressModal
        isOpen={showConfirmPress}
        onClose={() => { setShowConfirmPress(false); setPendingPress(null); }}
        onConfirm={handleConfirmPress}
        pending={pendingPress}
      />

      <ProfilePromptModal
        open={showProfilePrompt}
        onDismiss={() => { dismissProfilePrompt(); setShowProfilePrompt(false); }}
      />

      {pendingConflicts && (
        <AmbiguousIdentityModal
          conflicts={pendingConflicts.conflicts}
          onResolve={handleConflictResolved}
          onDismiss={handleConflictDismissed}
          isRetrying={isRetryingConflict}
        />
      )}

      <Dialog open={showClearConfirmDialog} onOpenChange={(open) => { if (!open) { setShowClearConfirmDialog(false); setCorrectionFromHole(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eraser className="w-5 h-5 text-red-500" />
              Clear Holes?
            </DialogTitle>
            <DialogDescription>
              {correctionFromHole !== null && gameState ? (() => {
                const po = getPlayingOrder(gameState.startingHole);
                const fromIdx = po.indexOf(correctionFromHole);
                const completed = new Set(getCompletedHolesInOrder(gameState));
                const toClear = po.filter((h, i) => i >= fromIdx && completed.has(h));
                return toClear.length === 1
                  ? `Clear scores for Hole ${correctionFromHole}. Any presses starting on this hole will be removed.`
                  : `Clear scores for Holes ${toClear.join(', ')}. Any presses that originated on these holes will be removed automatically.`;
              })() : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => { setShowClearConfirmDialog(false); setCorrectionFromHole(null); }}
              data-testid="button-cancel-clear-from"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmClearFrom}
              data-testid="button-confirm-clear-from"
            >
              Clear Scores
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAbandonDialog} onOpenChange={setShowAbandonDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Leave Round?
            </DialogTitle>
            <DialogDescription>
              You have scores entered for this round. Leaving now will take you back to setup but your round data will be preserved. You can resume later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowAbandonDialog(false)}
              data-testid="button-cancel-abandon"
            >
              Stay
            </Button>
            <Button
              variant="destructive"
              onClick={confirmAbandonRound}
              data-testid="button-confirm-abandon"
            >
              Leave Round
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNewRoundDialog} onOpenChange={setShowNewRoundDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-orange-500" />
              Start New Round?
            </DialogTitle>
            <DialogDescription>
              You have a match in progress. Starting a new round will erase all current scores, presses, and settings. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowNewRoundDialog(false)}
              data-testid="button-cancel-new-round"
            >
              Keep Playing
            </Button>
            <Button
              variant="destructive"
              onClick={handleNewRound}
              data-testid="button-confirm-new-round"
            >
              Start New Round
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showMatchCreatedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="match-created-overlay">
          <div className="bg-white rounded-2xl shadow-xl mx-6 max-w-sm w-full p-6 text-center">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-7 h-7 text-emerald-700" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-1" data-testid="text-match-created-title">Match Created!</h2>
            <p className="text-sm text-gray-500 mb-6">Share the live scorecard so your group can follow along.</p>
            <div className="space-y-3">
              {shareCopied ? (
                <div className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold py-3 px-4 rounded-xl">
                  <Check className="w-4 h-4" />
                  Link Copied!
                </div>
              ) : (
                <button
                  onClick={handleShareMatch}
                  disabled={!prefetchedInviteUrl || invitePrefetchError}
                  className="w-full flex items-center justify-center gap-2 bg-[#064E3B] text-white font-bold py-3 px-4 rounded-xl active:bg-emerald-800 disabled:opacity-50"
                  data-testid="button-share-live-scorecard"
                >
                  {!prefetchedInviteUrl && !invitePrefetchError ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Share2 className="w-4 h-4" />
                  )}
                  {invitePrefetchError ? 'Share Unavailable' : !prefetchedInviteUrl ? 'Preparing Link...' : 'Share Live Scorecard'}
                </button>
              )}
              <button
                onClick={() => setShowMatchCreatedModal(false)}
                className="w-full text-gray-500 font-medium py-2 px-4 rounded-xl active:bg-gray-100"
                data-testid="button-skip-to-scorecard"
              >
                Skip to Scorecard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Scorecard;
